// features/projects/templates/professional/widgets/data-provider.js
// ═══════════════════════════════════════════════════════════
// SHARED DASHBOARD DATA — fetched once at the dashboard level,
// distributed to every widget via context.
//
// The fetches are workspace-scoped. They mirror what the
// Continuum dashboard fetches today — comparisons, hypotheses,
// predictions, questions. Future: generalize for non-Continuum
// project types as their data shapes lock in.
//
// useWidgetData() returns:
//   { loading, error, comparisons, hypotheses, predictions,
//     questions, reload }
//
// Widgets that need only a subset of these can declare their
// dataRequirements in the registry; for now the provider fetches
// everything (small N) and widgets pull what they need.
// ═══════════════════════════════════════════════════════════

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const DashboardDataContext = createContext(null);

export function DashboardDataProvider({ workspaceId, children }) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    comparisons: [],
    hypotheses: [],
    predictions: [],
    questions: [],
  });

  const reload = useCallback(async () => {
    if (!workspaceId) {
      setState({ loading: false, error: null, comparisons: [], hypotheses: [], predictions: [], questions: [] });
      return;
    }
    setState(s => ({ ...s, loading: true }));
    try {
      const [compRes, hypRes, predRes, qRes] = await Promise.all([
        fetch(`/api/continuum/comparisons?workspace_id=${workspaceId}`),
        fetch(`/api/continuum/hypotheses/list?workspace_id=${workspaceId}`),
        fetch(`/api/continuum/predictions/list?workspace_id=${workspaceId}`),
        fetch(`/api/continuum/questions/list?workspace_id=${workspaceId}`),
      ]);
      const [compJ, hypJ, predJ, qJ] = await Promise.all([
        compRes.ok ? compRes.json() : { comparisons: [] },
        hypRes.ok  ? hypRes.json()  : { hypotheses: [] },
        predRes.ok ? predRes.json() : { predictions: [] },
        qRes.ok    ? qRes.json()    : { questions: [] },
      ]);
      setState({
        loading: false,
        error: null,
        comparisons: compJ.comparisons || [],
        hypotheses: hypJ.hypotheses || [],
        predictions: predJ.predictions || [],
        questions: qJ.questions || [],
      });
    } catch (err) {
      setState(s => ({ ...s, loading: false, error: err.message }));
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const value = { ...state, reload };
  return (
    <DashboardDataContext.Provider value={value}>
      {children}
    </DashboardDataContext.Provider>
  );
}

export function useWidgetData() {
  const ctx = useContext(DashboardDataContext);
  if (!ctx) {
    // Widget rendered outside a provider — return safe empty state
    // rather than crashing. Helps when widgets are previewed in a
    // catalog or rendered standalone for testing.
    return {
      loading: false, error: null,
      comparisons: [], hypotheses: [], predictions: [], questions: [],
      reload: () => {},
    };
  }
  return ctx;
}
